import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertDialog } from '../components/AlertDialog';
import { ConfirmModal } from '../components/ConfirmModal';

interface AlertOptions {
  title: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  buttonText?: string;
}

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary' | 'warning';
}

interface DialogContextValue {
  alert: (title: string, message: string, variant?: 'success' | 'error' | 'warning' | 'info') => void;
  confirm: (title: string, message: string, options?: Omit<ConfirmOptions, 'title' | 'message'>) => Promise<boolean>;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  // Alert state
  const [alertState, setAlertState] = useState<{
    visible: boolean;
    title: string;
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
    buttonText: string;
  }>({
    visible: false,
    title: '',
    message: '',
    variant: 'info',
    buttonText: 'OK',
  });

  // Confirm state
  const [confirmState, setConfirmState] = useState<{
    visible: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    variant: 'danger' | 'primary' | 'warning';
    resolve?: (value: boolean) => void;
  }>({
    visible: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'primary',
  });

  // Alert method
  const alert = useCallback((
    title: string,
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info' = 'info'
  ) => {
    setAlertState({
      visible: true,
      title,
      message,
      variant,
      buttonText: 'OK',
    });
  }, []);

  // Confirm method (promise-based)
  const confirm = useCallback((
    title: string,
    message: string,
    options?: Omit<ConfirmOptions, 'title' | 'message'>
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        visible: true,
        title,
        message,
        confirmText: options?.confirmText || 'Confirm',
        cancelText: options?.cancelText || 'Cancel',
        variant: options?.variant || 'primary',
        resolve,
      });
    });
  }, []);

  // Alert handlers
  const handleAlertDismiss = useCallback(() => {
    setAlertState((prev) => ({ ...prev, visible: false }));
  }, []);

  // Confirm handlers
  const handleConfirmAccept = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState((prev) => ({ ...prev, visible: false, resolve: undefined }));
  }, [confirmState.resolve]);

  const handleConfirmCancel = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState((prev) => ({ ...prev, visible: false, resolve: undefined }));
  }, [confirmState.resolve]);

  const contextValue: DialogContextValue = {
    alert,
    confirm,
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}

      {/* Alert Dialog */}
      <AlertDialog
        visible={alertState.visible}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        buttonText={alertState.buttonText}
        onDismiss={handleAlertDismiss}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        visible={confirmState.visible}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={handleConfirmAccept}
        onCancel={handleConfirmCancel}
      />
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextValue => {
  const context = useContext(DialogContext);
  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};
